import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GraduationCap, BookOpen, Clock, ShieldCheck, Mail, ArrowRight } from 'lucide-react';

const LandingPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // If already logged in, redirect to dashboard
    React.useEffect(() => {
        if (user) {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    return (
        <div className="min-h-screen bg-background text-foreground font-sans overflow-x-hidden">
            {/* Header / Navbar */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-background/70 backdrop-blur-xl border-b border-border/50">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center text-primary-foreground shadow-lg shadow-primary/20">
                            <GraduationCap size={24} />
                        </div>
                        <span className="text-2xl font-black tracking-tighter text-foreground">
                            my<span className="text-primary">dp</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-6">
                        <button
                            onClick={() => navigate('/login')}
                            className="px-6 py-2.5 bg-foreground text-background rounded-full font-bold hover:scale-105 transition-all shadow-xl shadow-foreground/10 flex items-center gap-2"
                        >
                            Log in <ArrowRight size={18} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="pt-40 pb-20 px-6">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-bold mb-8">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                        </span>
                        Academic Excellence, Automated.
                    </div>
                    <h1 className="text-6xl md:text-8xl font-black text-foreground leading-[0.95] tracking-tight mb-8 font-heading">
                        The ultimate <span className="text-primary">Academic</span> OS.
                    </h1>
                    <p className="max-w-2xl mx-auto text-xl text-muted-foreground font-medium mb-12 leading-relaxed">
                        mydp connects students, faculty, and administration in one unified platform. Manage attendance, timetables, and leave requests with zero friction.
                    </p>
                    <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                        <button
                            onClick={() => navigate('/login')}
                            className="w-full md:w-64 px-8 py-5 bg-primary text-primary-foreground rounded-2xl font-black text-lg hover:scale-105 transition-all shadow-2xl shadow-primary/20"
                        >
                            Get Started
                        </button>
                        <button className="w-full md:w-64 px-8 py-5 bg-background text-foreground border-2 border-border rounded-2xl font-black text-lg hover:bg-muted transition-all">
                            Learn More
                        </button>
                    </div>
                </div>
            </section>

            {/* Features Decorator */}
            <div className="max-w-4xl mx-auto h-[1px] bg-gradient-to-r from-transparent via-border to-transparent opacity-50"></div>

            {/* Features (Bento Grid Style) */}
            <section className="py-24 px-6 relative">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Attendance Card */}
                        <div className="p-10 bg-card rounded-[40px] border border-border/50 hover:border-primary/20 transition-all group">
                            <div className="w-14 h-14 bg-muted text-primary rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                                <ShieldCheck size={32} />
                            </div>
                            <h3 className="text-2xl font-black mb-4 font-heading">Smart Attendance</h3>
                            <p className="text-muted-foreground font-medium leading-relaxed">
                                Automated tracking for every class. Real-time updates for parents and students to ensure consistency across the board.
                            </p>
                        </div>

                        {/* Leave Management Card - Featured Dark */}
                        <div className="p-10 bg-foreground text-background rounded-[40px] shadow-2xl shadow-foreground/20 hover:scale-[1.02] transition-all relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 blur-[60px] rounded-full"></div>
                            <div className="w-14 h-14 bg-background/10 text-background rounded-2xl flex items-center justify-center mb-8">
                                <Mail size={32} />
                            </div>
                            <h3 className="text-2xl font-black mb-4 text-background font-heading">Leave Workflow</h3>
                            <p className="text-background/60 font-medium leading-relaxed">
                                Digital leave requests with multi-level approval. Instant notifications and seamless record keeping for the entire HOD team.
                            </p>
                        </div>

                        {/* Timetable Card */}
                        <div className="p-10 bg-primary text-primary-foreground rounded-[40px] shadow-2xl shadow-primary/20 hover:scale-[1.02] transition-all relative overflow-hidden">
                            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 blur-[40px] rounded-full"></div>
                            <div className="w-14 h-14 bg-white/20 text-primary-foreground rounded-2xl flex items-center justify-center mb-8">
                                <Clock size={32} />
                            </div>
                            <h3 className="text-2xl font-black mb-4 font-heading text-primary-foreground">Live Timetables</h3>
                            <p className="text-primary-foreground/80 font-medium leading-relaxed">
                                Never miss a class again. Personalized views for both students and faculty with instant schedule updates and slot management.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-20 px-6 bg-muted/30">
                <div className="max-w-7xl mx-auto border-t border-border pt-20">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-12">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center text-primary-foreground">
                                    <GraduationCap size={20} />
                                </div>
                                <span className="text-2xl font-black tracking-tighter">my<span className="text-primary">dp</span></span>
                            </div>
                            <p className="text-muted-foreground font-medium max-w-sm">
                                Building a state-of-the-art ecosystem for modern academic institutions.
                            </p>
                        </div>
                        <div className="flex gap-12">
                            <div className="flex flex-col gap-4 text-muted-foreground font-bold hover:text-primary transition-colors cursor-pointer">Terms</div>
                            <div className="flex flex-col gap-4 text-muted-foreground font-bold hover:text-primary transition-colors cursor-pointer">Privacy</div>
                            <div className="flex flex-col gap-4 text-muted-foreground font-bold hover:text-primary transition-colors cursor-pointer">Contact</div>
                        </div>
                        <div className="text-muted-foreground font-medium text-sm">
                            © 2026 HUBVERSE. Engineered for excellence.
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
